import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import {
  Plus,
  Loader2,
  MoreVertical,
  Trash2,
  ArrowRight,
  Flame,
  Copy,
  Layers,
  Info,
  Factory,
  CheckCircle2,
  Scissors,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canOrder, canProdEdit } from "@/lib/orderPerms";
import ProductionPlanDialog from "@/components/ProductionPlanDialog";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import CuttingRecordDialog from "@/components/CuttingRecordDialog";
import QualityControlDialog from "@/components/QualityControlDialog";
import InvoiceOrderDialog from "@/components/InvoiceOrderDialog";
import { KanbanHScroll } from "@/components/KanbanHScroll";
import { FinishToStockDialog } from "@/components/FinishToStockDialog";
import { lineMethod } from "@/lib/lineMath";
import { ProductThumb, PlanRemaining, VariantChips, StageProgress, buildDistribution } from "@/components/ProductionCardParts";

// Kanban aşaması → departman işi (ilerleme yalnız gerçek department_jobs verisinden)
const STAGE_DEPT = { kesim: "kesim", baski: "baski", nakis: "nakis", dikim: "dikim", son_kalite: "kalite", paketleme: "paketleme" };

const PRIORITY_META = {
  dusuk: { label: "Düşük", classes: "bg-slate-800 text-slate-300 border-slate-700" },
  normal: { label: "Normal", classes: "bg-blue-950/60 text-blue-300 border-blue-900" },
  yuksek: { label: "Yüksek", classes: "bg-blue-950/60 text-blue-300 border-blue-900" },
  acil: { label: "ACİL", classes: "bg-red-950/70 text-red-300 border-red-900" },
};

const empty = {
  customer_id: "",
  product_name: "",
  quantity: 100,
  unit_price: 0,
  due_date: "",
  priority: "normal",
  notes: "",
};

const APP_STAGES = ["baski", "nakis"];
const BOM_STAGES = ["pastal", "kesim", "dikim"];
const isAppOnlyLine = (order, li) => (li.supply_type || "").toLowerCase() === "traded"
  || ["stock", "ready_purchase"].includes(lineMethod(order, li));

export default function Production() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stages, setStages] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [bomOrder, setBomOrder] = useState(null); // opens ProductionPlanDialog when set
  const [detailOrder, setDetailOrder] = useState(null); // opens OrderDetailDialog
  const [cutOrder, setCutOrder] = useState(null); // opens CuttingRecordDialog (kesim aşaması)
  const [qcOrder, setQcOrder] = useState(null); // opens QualityControlDialog (son_kalite aşaması)
  const [invOrder, setInvOrder] = useState(null); // opens InvoiceOrderDialog (irsaliye_fatura aşaması)
  const [meta, setMeta] = useState({ products: {}, line_labels: {}, jobs: {} }); // kart sunum verisi (salt-okunur)
  const lineLabel = (l) => meta.line_labels[l.id] || "";
  const sbPairs = (sb) => Object.entries(sb || {}).filter(([, q]) => Number(q) > 0);
  const cardMeta = (o, line, effStage) => {
    const all = o.line_items || [];
    const pid = line ? line.product_id : (o.product_id || all[0]?.product_id);
    const p = meta.products[pid] || {};
    let dist = null;
    if (line?._groupLines) dist = buildDistribution(line._groupLines.map((l) => [lineLabel(l), l.quantity]));
    else if (line) dist = sbPairs(line.size_breakdown).length ? buildDistribution(sbPairs(line.size_breakdown)) : buildDistribution([[lineLabel(line), line.quantity]]);
    else if (sbPairs(o.size_breakdown).length) dist = buildDistribution(sbPairs(o.size_breakdown));
    else if (all.length && new Set(all.map((l) => l.product_id)).size === 1) dist = buildDistribution(all.map((l) => [lineLabel(l), l.quantity]));
    const dept = STAGE_DEPT[effStage];
    const ids = line ? new Set((line._groupLines || [line]).map((l) => l.id)) : null;
    const jobs = dept ? (meta.jobs[o.id] || []).filter((j) => j.department === dept && (!ids || ids.has(j.line_id))) : [];
    const total = jobs.reduce((a, j) => a + (Number(j.effective_quantity || j.quantity) || 0), 0);
    const done = jobs.reduce((a, j) => a + (Number(j.completed_quantity) || 0), 0);
    return { pid, code: p.code, imageId: p.image_id, dist, progress: total > 0 ? { done, total } : null };
  };
  const { can } = useAuth();
  // Aşama bazlı üretim yetkisi — backend production.stage_<key> / production.manage
  // ile zorunlu kontrol eder; burada yalnız buton disabled/gizli (UX).
  const STAGE_FREE = new Set(["teklif", "siparis_onay", "irsaliye_fatura", "sevkiyat", "tamamlandi", "iptal", "reddedildi"]);
  const canStage = (s) => STAGE_FREE.has(s) || can("production", "manage") || can("production", `stage_${s}`);
  const NO_PERM_TITLE = "Bu aşamada işlem yapma yetkiniz yok";
  const NOT_STARTED_TITLE = "Bu sipariş henüz üretime başlatılmamış. Önce Siparişler ekranından Üretime Başla işlemini yapın.";
  // Backend kuralının UX yansıması (asıl zorunluluk backend'de): Üretime Başla kanıtı veya eski üretim kaydı
  const orderStarted = (o) => (o.line_items || []).some((li) => li.production_started || (li.production_stage && !STAGE_FREE.has(li.production_stage)))
    || !STAGE_FREE.has(o.stage);
  // Baskı/Nakış zorunlu operasyon — atlanamaz (asıl kontrol backend'de)
  const skippedApp = (o, ss) => {
    const all = stages.concat(["tamamlandi", "iptal"]);
    const ti = all.indexOf(ss);
    if (ss === "iptal" || ti < 0) return null;
    for (const li of o.line_items || []) {
      const deps = new Set((li.applications || []).filter((a) => a.send_to_production && Number(a.quantity) > 0).map((a) => a.production_department_id));
      const ls = li.production_stage && all.indexOf(li.production_stage) > all.indexOf(o.stage) ? li.production_stage : o.stage;
      const ci = all.indexOf(ls);
      if (STAGE_FREE.has(ls) && STAGE_FREE.has(ss)) continue;
      const hit = APP_STAGES.find((d) => deps.has(d) && ci < all.indexOf(d) && all.indexOf(d) < ti);
      if (hit) return hit;
    }
    return null;
  };
  // Kalem menüsü: serbest aşamalar dahil taşıma yalnız üretim write yetkisiyle (UX; backend kuralı değişmedi)
  const lineWrite = canProdEdit(can) || stages.some((s) => !STAGE_FREE.has(s) && canStage(s));
  const canMove = (o, ss) => canStage(ss) && (STAGE_FREE.has(ss) || orderStarted(o)) && !skippedApp(o, ss);
  const moveTitle = (o, ss) => (!canStage(ss) ? NO_PERM_TITLE : skippedApp(o, ss) ? `${labels[skippedApp(o, ss)]} zorunlu üretim operasyonu — atlanamaz` : !canMove(o, ss) ? NOT_STARTED_TITLE : undefined);

  const load = async () => {
    api.get("/production/kanban-meta").then(({ data }) => setMeta(data)).catch(() => {});
    try {
      const [o, c, m] = await Promise.all([
        api.get("/orders"),
        api.get("/customers"),
        api.get("/meta/stages"),
      ]);
      setOrders(o.data);
      setCustomers(c.data);
      // Teklifler üretim sürecine dahil değildir — Kanbanda kolon yok
      setStages((m.data.stages || []).filter((st) => st !== "teklif"));
      setLabels(m.data.labels);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Deep-link: /uretim?order_id=... → sipariş detay dialogunu aç
  // (ör. muhasebe "Fatura Hazır" bildirimi tıklaması)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const oid = searchParams.get("order_id");
    if (!oid) return;
    setSearchParams({}, { replace: true });
    api.get(`/orders/${oid}`)
      .then(({ data }) => setDetailOrder(data))
      .catch(() => toast.error("Sipariş bulunamadı"));
  }, [searchParams]); // eslint-disable-line

  // Kalem bazlı kartlar: üretime başlamış her line_item kendi
  // production_stage sütununda görünür; BAŞLAMAMIŞ kalemler başlangıç/BOM
  // aşamasında (order.stage) "Üretim Başlamadı" kartıyla erişilebilir kalır;
  // satırsız/legacy siparişler tek kart (mevcut davranış).
  // EK: satinalma/malzeme_kabul/kumas_kontrol aşamalarında çok kalemli sipariş
  // HER ZAMAN kalem kartlarına bölünür (malzeme girişi kalem bazlı ilerler;
  // order-level İleri bu siparişlerde render edilmez).
  const LINE_MODE_STAGES = ["satinalma", "malzeme_kabul", "kumas_kontrol"];
  const grouped = useMemo(() => {
    const map = {};
    stages.forEach((s) => (map[s] = []));
    // Ticari kalem + üretime gidecek application yok → Üretim Takibi'ne girmez.
    const needsProd = (li) => (li.supply_type || "").toLowerCase() !== "traded"
      || (li.applications || []).some((a) => a.send_to_production && Number(a.quantity) > 0);
    orders.forEach((o) => {
      const allLines = o.line_items || [];
      const lines = allLines.filter(needsProd);
      if (allLines.length && !lines.length) return;
      if (lines.length > 1 && LINE_MODE_STAGES.includes(o.stage)) {
        const anyFlag = lines.some((li) => li.production_started);
        lines.forEach((li) => {
          const st = li.production_stage || o.stage;
          const bucket = map[st] || map[o.stage];
          if (bucket) bucket.push({ order: o, line: anyFlag && !li.production_started ? { ...li, _notStarted: true } : li });
        });
        return;
      }
      const startedLines = lines.filter((li) => li.production_started);
      if (startedLines.length) {
        // Aynı Baskı/Nakış group_id'li kalemler kendi operasyon aşamasında TEK kart
        const appGroups = {};
        startedLines.forEach((li) => {
          const st = li.production_stage || o.stage;
          const pApps = (li.applications || []).filter((a) => a.group_id && a.send_to_production);
          // Hazır Alım/ticari kalem grubu tüm aşamalarda bütün kalır; üretilen kalem yalnız uygulama aşamasında
          const gid = (pApps.find((a) => a.production_department_id === st)
            || (isAppOnlyLine(o, li) ? pApps[0] : null))?.group_id;
          if (gid) {
            const k = `${st}::${gid}`;
            if (!appGroups[k]) {
              appGroups[k] = { ...li, quantity: 0, variant_sku: "", variant_attributes: {}, _groupLines: [] };
              const bucket = map[st] || map[o.stage];
              if (bucket) bucket.push({ order: o, line: appGroups[k] });
            }
            appGroups[k].quantity += Number(li.quantity) || 0;
            appGroups[k]._groupLines.push(li);
            return;
          }
          const bucket = map[st] || map[o.stage];
          if (bucket) bucket.push({ order: o, line: li });
        });
        lines.filter((li) => !li.production_started).forEach((li) => {
          if (map[o.stage]) map[o.stage].push({ order: o, line: { ...li, _notStarted: true } });
        });
      } else if (map[o.stage]) {
        map[o.stage].push({ order: o, line: null });
      }
    });
    return map;
  }, [orders, stages]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.product_name || !form.quantity) return;
    setSaving(true);
    try {
      await api.post("/orders", {
        ...form,
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price) || 0,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      });
      toast.success("Sipariş oluşturuldu");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const advance = async (order) => {
    const idx = stages.indexOf(order.stage);
    if (idx < 0 || idx >= stages.length - 1) return;
    const next = stages[idx + 1];
    try {
      const { data } = await api.patch(`/orders/${order.id}/stage`, { stage: next });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`${data.order_no} → ${labels[next]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Kalem bazlı İLERİ — yalnız ilgili line_item ilerler, diğer kalemler
  // ve (tümü eşitlenmedikçe) order.stage değişmez.
  // Seçilmeyen Baskı/Nakış aşaması ve Hazır Alım kalemde Kesim/Dikim atlanır
  const nextLineStage = (order, line) => {
    const idx = stages.indexOf(line.production_stage || order.stage);
    if (idx < 0 || idx >= stages.length - 1) return null;
    const appDepts = new Set((line.applications || []).filter((a) => a.send_to_production).map((a) => a.production_department_id));
    const appOnly = isAppOnlyLine(order, line);
    const skip = (st) => (appDepts.size > 0 && APP_STAGES.includes(st) && !appDepts.has(st))
      || (appOnly && BOM_STAGES.includes(st));
    let n = idx + 1;
    while (n < stages.length - 1 && skip(stages[n])) n += 1;
    return stages[n];
  };

  const advanceLine = async (order, line) => {
    const next = nextLineStage(order, line);
    if (!next) return;
    try {
      let data;
      for (const l of (line._groupLines || [line])) {
        ({ data } = await api.patch(`/orders/${order.id}/lines/${l.id}/stage`, { stage: next }));
      }
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`${order.order_no} · ${line.product_name} → ${labels[next]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Kalem kartı menüsü: yalnız o kalem(ler) kontrollü satır ucu ile taşınır (backend kuralları geçerli)
  const moveLineTo = async (order, line, stage) => {
    try {
      let data;
      for (const l of (line._groupLines || [line])) {
        ({ data } = await api.patch(`/orders/${order.id}/lines/${l.id}/stage`, { stage }));
      }
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`${order.order_no} · ${line.product_name} → ${labels[stage]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveTo = async (order, stage) => {
    try {
      const { data } = await api.patch(`/orders/${order.id}/stage`, { stage });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`${data.order_no} → ${labels[stage]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (order) => {
    if (!window.confirm(`${order.order_no} siparişini silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/orders/${order.id}`);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      toast.success("Sipariş silindi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicate = async (order) => {
    try {
      const { data } = await api.post(`/orders/${order.id}/duplicate`);
      setOrders((prev) => [data, ...prev]);
      toast.success(`Kopyalandı → ${data.order_no}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // FAZ 1 — MTS: "Stoka Al" (finish-to-stock). Sadece production_type="stock"
  // ve stock_finished_at boş olan emirlerde gösterilir. İdempotency backend'de
  // atomik claim ile korunuyor; yine de burada da guard var.
  const [stockOrder, setStockOrder] = useState(null);
  const finishToStock = (order) => {
    if (order.production_type !== "stock" || order.stock_finished_at) return;
    setStockOrder(order);
  };
  const refreshOrder = async (id) => {
    const { data: fresh } = await api.get(`/orders/${id}`);
    setOrders((prev) => prev.map((o) => (o.id === id ? fresh : o)));
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
    } catch {
      return null;
    }
  };

  const isDelayed = (iso) => {
    if (!iso) return false;
    try {
      return new Date(iso) < new Date();
    } catch {
      return false;
    }
  };

  return (
    <div>
      <PageHeader
        title="Üretim Takibi"
        subtitle="Tekliften sevkiyata kadar 20 aşamalı canlı akış"
        breadcrumbs={["Modüller", "Üretim Takibi"]}
        actions={can("orders", "add") && (
          <Button
            data-testid="add-order-btn"
            onClick={() => setOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> Yeni Sipariş
          </Button>
        )}
      />

      <div className="p-4 lg:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
          <KanbanHScroll testid="kanban-board">
            <div className="flex gap-3 min-w-max">
              {stages.map((s, idx) => {
                const items = grouped[s] || [];
                const isCompleted = s === "tamamlandi";
                return (
                  <div
                    key={s}
                    data-testid={`stage-column-${s}`}
                    className="w-72 shrink-0 bg-slate-900/70 border border-slate-800 rounded-sm flex flex-col"
                  >
                    <div className={`px-3 py-2.5 border-b flex items-center justify-between sticky top-0 z-10 ${
                      isCompleted ? "bg-emerald-950/60 border-emerald-900" : "bg-slate-900 border-slate-800"
                    }`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-5 h-5 flex items-center justify-center text-[10px] font-bold font-mono rounded-sm ${
                          isCompleted ? "bg-emerald-600 text-white" : "bg-blue-600 text-white"
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-200 truncate">
                          {labels[s]}
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-slate-400 shrink-0 tabular-nums">
                        {items.length}
                      </div>
                    </div>
                    <div className="p-2 space-y-2 min-h-24 max-h-[calc(100vh-260px)] overflow-y-auto kanban-scroll">
                      {items.length === 0 ? (
                        <div className="text-[10px] text-slate-600 font-mono uppercase tracking-widest text-center py-4">
                          — Boş —
                        </div>
                      ) : (
                        items.map(({ order: o, line }) => {
                          const pr = PRIORITY_META[o.priority] || PRIORITY_META.normal;
                          const delayed = isDelayed(o.due_date) && !isCompleted;
                          // Kalem kartı: başlamış line_item kendi aşamasında
                          const effStage = line ? (line.production_stage || o.stage) : o.stage;
                          const cardKey = line ? `${o.id}::${line.id}` : o.id;
                          const notStarted = !!line?._notStarted;
                          const cm = cardMeta(o, line, effStage);
                          return (
                            <Card
                              key={cardKey}
                              data-testid={line ? `line-card-${o.id}-${line.id}` : `order-card-${o.id}`}
                              className={`bg-slate-950 border rounded-sm p-3 hover:border-slate-600 transition-colors ${
                                delayed ? "border-red-900/60" : "border-slate-800"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                  <div className="text-[10px] font-mono uppercase tracking-widest text-blue-500">
                                    {o.order_no}
                                  </div>
                                  <div className="text-base font-bold text-white line-clamp-2 leading-tight mt-0.5" data-testid={`order-card-customer-${cardKey}`}>
                                    {o.production_type === "stock" ? "Stok Üretimi" : o.customer_name}
                                  </div>
                                </div>
                                {(
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      data-testid={line ? `line-menu-${cardKey}` : `order-menu-${o.id}`}
                                      className="text-slate-500 hover:text-slate-200 shrink-0 p-0.5"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-200 rounded-sm w-56">
                                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                                      {line ? "Kalemi Aşamaya Taşı" : "Aşamaya Taşı"}
                                    </DropdownMenuLabel>
                                    <div className="max-h-64 overflow-y-auto">
                                      {stages.map((ss) => {
                                        const ok = canMove(o, ss) && (!line || lineWrite);
                                        return (
                                        <DropdownMenuItem
                                          key={ss}
                                          data-testid={line ? `line-move-${cardKey}-${ss}` : undefined}
                                          onClick={() => (line ? moveLineTo(o, line, ss) : moveTo(o, ss))}
                                          disabled={ss === (line ? (line.production_stage || o.stage) : o.stage) || !ok || (!!line?._notStarted && !STAGE_FREE.has(ss))}
                                          title={line && !lineWrite ? NO_PERM_TITLE : moveTitle(o, ss)}
                                          className={`text-xs ${ok ? "cursor-pointer" : "cursor-not-allowed text-slate-600"} ${ss === o.stage ? "text-blue-500" : ""}`}
                                        >
                                          {labels[ss]}
                                        </DropdownMenuItem>
                                        );
                                      })}
                                    </div>
                                    <DropdownMenuSeparator className="bg-slate-800" />
                                    {canOrder(can, "add", o) && <DropdownMenuItem
                                      data-testid={`duplicate-${o.id}`}
                                      onClick={() => duplicate(o)}
                                      className="text-xs text-blue-300 focus:text-blue-200 cursor-pointer"
                                    >
                                      <Copy className="w-3 h-3 mr-2" /> {line ? "Siparişi Kopyala" : "Kopyala"}
                                    </DropdownMenuItem>}
                                    {canOrder(can, "delete", o) && <DropdownMenuItem
                                      data-testid={`delete-${o.id}`}
                                      onClick={() => remove(o)}
                                      className="text-xs text-red-400 focus:text-red-300 cursor-pointer"
                                    >
                                      <Trash2 className="w-3 h-3 mr-2" /> {line ? "Siparişi Sil" : "Sil"}
                                    </DropdownMenuItem>}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                )}
                              </div>

                              <div className="flex gap-2.5 mb-1.5">
                                <ProductThumb productId={cm.pid} imageId={cm.imageId} testId={`card-thumb-${cardKey}`} />
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-semibold text-slate-100 line-clamp-2 leading-snug" data-testid={`order-card-product-${cardKey}`}>
                                    {line ? line.product_name : o.product_name}
                                  </div>
                                  <div className="text-[10px] font-mono text-slate-500 truncate mt-0.5" data-testid={`card-product-code-${cardKey}`}>
                                    {cm.code || "Kod —"}
                                  </div>
                                </div>
                              </div>
                              <div className="mb-2">
                                <PlanRemaining due={o.due_date} done={effStage === "tamamlandi"} testId={`card-plan-${cardKey}`} />
                              </div>

                              <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                                {/* Tedarik yöntemi rozeti — mevcut gerçek veri (lineMethod / supply_type / outsourcing) */}
                                {(() => {
                                  const pm = line ? lineMethod(o, line) : (o.production_method || "internal");
                                  const out = (line && line.outsourcing) || o.outsourcing || null;
                                  const isFason = pm === "outsourced" && out;
                                  const isStock = pm === "stock";
                                  const isReady = line
                                    ? ((line.supply_type || "").toLowerCase() === "traded" || pm === "ready_purchase")
                                    : pm === "ready_purchase";
                                  return (
                                    <>
                                      <span data-testid={`production-method-badge-${cardKey}`} className={`stage-badge border ${
                                        isReady || isStock
                                          ? "text-emerald-300 bg-emerald-950/50 border-emerald-900/60"
                                          : isFason
                                          ? "text-amber-300 bg-amber-950/50 border-amber-900/60"
                                          : "text-blue-300 bg-blue-950/50 border-blue-900/60"
                                      }`}>
                                        {isStock ? "Stoktan Satış" : isReady ? "Hazır Alım" : isFason ? "Fason Üretim" : pm === "mixed" ? "Karma Tedarik" : "İç Üretim"}
                                      </span>
                                      {isFason && (
                                        <span className="text-[9px] font-mono text-amber-200/80 w-full truncate" data-testid={`fason-info-${cardKey}`}>
                                          {out.supplier_name}
                                          {out.delivery_date && <> · teslim {out.delivery_date}</>}
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                                <span data-testid={`card-priority-${cardKey}`} className={`stage-badge border ${pr.classes}`}>
                                  {o.priority === "acil" && <Flame className="w-2.5 h-2.5" />}
                                  {pr.label}
                                </span>
                                {(() => {
                                  // "Malzeme Kabul 1/4" — siparişin kaç kalemi bu aşamayı geçti
                                  const oLines = o.line_items || [];
                                  if (oLines.length <= 1 || !LINE_MODE_STAGES.includes(s)) return null;
                                  const sIdx = stages.indexOf(s);
                                  const done = oLines.filter((li) => stages.indexOf(li.production_stage || o.stage) > sIdx).length;
                                  return (
                                    <span
                                      data-testid={`line-progress-${cardKey}`}
                                      className="stage-badge border bg-indigo-950/50 text-indigo-300 border-indigo-900"
                                      title={`${oLines.length} kalemden ${done} tanesi bu aşamayı geçti`}
                                    >
                                      {labels[s]} {done}/{oLines.length}
                                    </span>
                                  );
                                })()}
                                {notStarted && (
                                  <span
                                    data-testid={`not-started-badge-${cardKey}`}
                                    className="stage-badge border bg-slate-800/80 text-slate-400 border-slate-700"
                                  >
                                    Üretim Başlamadı
                                  </span>
                                )}
                                {!line && Number(o.invoiced_total || 0) > 0 && Number(o.shipped_total || 0) > 0 && (
                                  Number(o.invoiced_total || 0) >= Number(o.shipped_total || 0) ? (
                                    <span
                                      data-testid={`invoiced-badge-${o.id}`}
                                      className="stage-badge border bg-emerald-950/60 text-emerald-300 border-emerald-900"
                                    >
                                      Faturalandı
                                    </span>
                                  ) : (
                                    <span
                                      data-testid={`partial-invoice-badge-${o.id}`}
                                      className="stage-badge border bg-cyan-950/60 text-cyan-300 border-cyan-900"
                                    >
                                      Kısmi Fatura {o.invoiced_total}/{o.shipped_total}
                                    </span>
                                  )
                                )}
                              </div>

                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="text-sm font-bold text-white tabular-nums" data-testid={`card-total-${cardKey}`}>
                                  Toplam: {line ? line.quantity : o.quantity} Adet
                                </div>
                              </div>
                              <div className="mb-2">
                                <VariantChips dist={cm.dist} testId={`card-variants-${cardKey}`} />
                              </div>
                              {!notStarted && cm.progress && (
                                <div className="mb-2">
                                  <StageProgress label={labels[effStage]} done={cm.progress.done} total={cm.progress.total} testId={`card-progress-${cardKey}`} />
                                </div>
                              )}

                              {/* Kalem bazlı üretim durumu (çok kalemli sipariş — yalnız legacy tek kartta) */}
                              {!line && (o.line_items || []).length > 1 && (() => {
                                const lst = o.line_items.map((li) => ({
                                  name: li.product_name,
                                  stage: li.production_stage || o.stage,
                                  started: !!li.production_started,
                                }));
                                const distinct = new Set(lst.map((x) => x.stage));
                                return (
                                  <div className="mt-1 space-y-0.5" data-testid={`line-stages-${o.id}`}>
                                    {distinct.size > 1 && (
                                      <span className="inline-block text-[9px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-400 border border-amber-900/60 rounded-sm px-1.5 py-0.5" data-testid={`partial-production-${o.id}`}>
                                        Kısmi Üretimde
                                      </span>
                                    )}
                                    <div className="text-[10px] text-slate-500 font-mono">{o.line_items.length} ürün</div>
                                    {lst.slice(0, 3).map((x, i) => (
                                      <div key={i} className="text-[10px] font-mono text-slate-400 truncate" data-testid={`line-stage-${o.id}-${i}`}>
                                        • {x.name} — <span className={x.started ? "text-blue-400" : "text-slate-500"}>{labels[x.stage] || x.stage}</span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}

                              <div className="flex items-center justify-end flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono pt-2 border-t border-slate-800/80">
                                <>
                                  {!notStarted && effStage === "kesim" && canStage("kesim") && (
                                    <button
                                      data-testid={`cutting-record-${cardKey}`}
                                      onClick={() => setCutOrder(o)}
                                      className="flex items-center gap-1 text-amber-400 hover:text-amber-300 uppercase tracking-wider font-semibold transition-colors"
                                      title="Gerçek kesilen adetleri beden bazında gir (fazla kesim yönetimi)"
                                    >
                                      <Scissors className="w-3 h-3" /> Kesim Kaydı
                                    </button>
                                  )}
                                  {!notStarted && effStage === "son_kalite" && canStage("son_kalite") && (
                                    <button
                                      data-testid={`quality-record-${cardKey}`}
                                      onClick={() => setQcOrder(o)}
                                      className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 uppercase tracking-wider font-semibold transition-colors"
                                      title="Sağlam/defolu adetleri gir — nihai sevk adedini belirle"
                                    >
                                      <ShieldCheck className="w-3 h-3" /> Kalite Kaydı
                                    </button>
                                  )}
                                  {!line && o.stage === "irsaliye_fatura" && can("finance", "add") && Math.max(Number(o.shipped_total || 0), Number(o.quantity || 0)) > Number(o.invoiced_total || 0) && (
                                    <button
                                      data-testid={`invoice-order-${o.id}`}
                                      onClick={() => setInvOrder(o)}
                                      className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 uppercase tracking-wider font-semibold transition-colors"
                                      title="Sevk edilmiş miktarları faturala (kaynak siparişin aşaması değişmez)"
                                    >
                                      <FileText className="w-3 h-3" /> Faturala
                                    </button>
                                  )}
                                  <button
                                    data-testid={`detail-${cardKey}`}
                                    onClick={() => setDetailOrder(o)}
                                    className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 uppercase tracking-wider font-semibold transition-colors"
                                    title="Sipariş detayları (baskı, logo, adet, beden)"
                                  >
                                    <Info className="w-3 h-3" /> Detay
                                  </button>
                                  <button
                                    data-testid={`bom-view-${cardKey}`}
                                    onClick={() => setBomOrder(o)}
                                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 uppercase tracking-wider font-semibold transition-colors"
                                    title="Malzeme listesini görüntüle"
                                  >
                                    <Layers className="w-3 h-3" />
                                    {/* Üretim başladıysa yeniden başlatma yerine görüntüleme */}
                                    {((o.line_items || []).some((li) => li.production_started) ||
                                      ["kesim", "baski", "nakis", "dikim", "son_kalite", "paketleme",
                                       "koli_hazirlik", "irsaliye_fatura", "sevkiyat", "tamamlandi"].includes(o.stage))
                                      ? "Üretimi Görüntüle"
                                      : "Ürün Reçetesi"}
                                  </button>
                                  {!line && o.production_type === "stock" && !o.stock_finished_at && canProdEdit(can) && (
                                    <button
                                      data-testid={`stoka-al-${o.id}`}
                                      onClick={() => finishToStock(o)}
                                      disabled={stages.indexOf(o.stage) < stages.indexOf("paketleme")}
                                      className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 disabled:text-slate-600 disabled:cursor-not-allowed uppercase tracking-wider font-semibold transition-colors"
                                      title={stages.indexOf(o.stage) < stages.indexOf("paketleme") ? "Stoka Al yalnız Paketleme ve sonrası aşamalarda yapılabilir" : "Tamamlanan adetleri beden bazında seçili depoya stok gir"}
                                    >
                                      <Factory className="w-3 h-3" /> Stoka Al
                                    </button>
                                  )}
                                  {!line && o.production_type === "stock" && o.stock_finished_at && (
                                    <span
                                      data-testid={`stoka-alindi-${o.id}`}
                                      className="flex items-center gap-1 text-emerald-400 uppercase tracking-wider font-semibold"
                                      title={`Stoka alındı: ${fmtDate(o.stock_finished_at) || ""}`}
                                    >
                                      <CheckCircle2 className="w-3 h-3" /> Stoka Alındı
                                    </span>
                                  )}
                                  {!notStarted && !(line ? effStage === stages[stages.length - 1] : isCompleted) && (() => {
                                    const _next = line ? nextLineStage(o, line) : stages[stages.indexOf(effStage) + 1];
                                    const _ok = line ? canStage(_next) && lineWrite : canMove(o, _next);
                                    return (
                                    <button
                                      data-testid={`advance-${cardKey}`}
                                      disabled={!_ok}
                                      title={_ok ? undefined : line ? NO_PERM_TITLE : moveTitle(o, _next)}
                                      onClick={() => (line ? advanceLine(o, line) : advance(o))}
                                      className={`flex items-center gap-1 uppercase tracking-wider font-semibold transition-colors ${
                                        _ok ? "text-blue-500 hover:text-blue-400" : "text-slate-600 cursor-not-allowed"
                                      }`}
                                    >
                                      İleri <ArrowRight className="w-3 h-3" />
                                    </button>
                                    );
                                  })()}
                                </>
                              </div>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </KanbanHScroll>
          <FinishToStockDialog order={stockOrder} onClose={() => setStockOrder(null)} onDone={() => stockOrder && refreshOrder(stockOrder.id)} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">Yeni Sipariş</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Müşteri *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="order-customer-select" className="bg-slate-950 border-slate-700 rounded-sm">
                  <SelectValue placeholder="Müşteri seçin" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Ürün Adı *</Label>
              <Input
                data-testid="order-product-input"
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                required
                placeholder="Örn: Yansıtıcılı Yelek"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Adet *</Label>
                <Input
                  data-testid="order-qty-input"
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  required
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Birim Fiyat (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Öncelik</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="dusuk">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="yuksek">Yüksek</SelectItem>
                    <SelectItem value="acil">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Termin Tarihi</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Notlar</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
              >
                İptal
              </Button>
              <Button
                data-testid="save-order-btn"
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Material list (BOM) preview + Purchase order draft */}
      <ProductionPlanDialog
        order={bomOrder}
        open={!!bomOrder}
        onOpenChange={(v) => !v && setBomOrder(null)}
        onDone={() => { setBomOrder(null); load(); }}
        labels={labels}
      />

      {/* Order detail (items, logos, print) */}
      <OrderDetailDialog
        order={detailOrder}
        open={!!detailOrder}
        onOpenChange={(v) => !v && setDetailOrder(null)}
      />

      {/* Kesim Kaydı (fazla kesim yönetimi) — sipariş adedi değişmez */}
      <CuttingRecordDialog
        order={cutOrder}
        open={!!cutOrder}
        onOpenChange={(v) => !v && setCutOrder(null)}
        onSaved={load}
      />

      {/* Kalite Kontrol — nihai sevk edilebilir adet (idempotent apply) */}
      <QualityControlDialog
        order={qcOrder}
        open={!!qcOrder}
        onOpenChange={(v) => !v && setQcOrder(null)}
        onSaved={load}
      />

      {/* Faturala — sevk edilen miktarlar için ayrı FT fatura (kaynak aşama değişmez) */}
      <InvoiceOrderDialog
        order={invOrder}
        open={!!invOrder}
        onOpenChange={(v) => !v && setInvOrder(null)}
        onSaved={load}
      />
    </div>
  );
}
